/**
 * Silhouette detail — the stuff that stops a roofline being a straight line and
 * a wall being a flat plane: AC units, dishes, antennas, water tanks, cables,
 * laundry, drainpipes, awnings, signage, conduit, rebar.
 *
 * owner: level
 */
import * as THREE from 'three';
import {
  chamferBox, chamferCyl, taperCyl, xform, alignX, segLength, catenary, tube,
  quadXY, quadXZ, stripAttrs, blade,
} from '../Geo.js';
import { driftPatch, crackScatter } from './Street.js';

/* ------------------------------------------------------------------ */
/* roof furniture                                                      */
/* ------------------------------------------------------------------ */

export function acUnit(w, { x, y, z, rotY = 0, scale = 1 }) {
  const s = scale;
  const body = chamferBox(0.86 * s, 0.62 * s, 0.42 * s, 0.03);
  w.batch.add(body, 'metal_painted', { matrix: xform([x, y + 0.31 * s, z], rotY), surface: 'metal', uvScale: 0.5, tint: [0.92, 0.93, 0.9] });
  body.dispose();
  const fan = chamferCyl(0.24 * s, 0.06, 14, 0.01);
  fan.rotateX(Math.PI / 2);
  w.batch.add(fan, 'metal_grate', { matrix: xform([x, y + 0.34 * s, z + 0.22 * s], rotY), surface: 'metal', uvScale: 0.3, cast: false });
  fan.dispose();
  for (const dx of [-0.34, 0.34]) {
    const br = chamferBox(0.07, 0.12, 0.5 * s, 0.012);
    const px = x + Math.cos(rotY) * dx * s, pz = z - Math.sin(rotY) * dx * s;
    w.batch.add(br, 'metal_rusted', { matrix: xform([px, y + 0.06, pz], rotY), surface: 'metal', uvScale: 0.3 });
    br.dispose();
  }
  // condensate line dangling off the back
  const p0 = [x - Math.sin(rotY) * 0.2, y + 0.12, z - Math.cos(rotY) * 0.2];
  const t = tube([p0, [p0[0], p0[1] - 0.35, p0[2] - 0.1], [p0[0] + 0.15, p0[1] - 0.62, p0[2] - 0.05]], 0.016, 5);
  w.batch.add(t, 'plastic_crate', { surface: 'plastic', uvScale: 0.3, tint: [0.6, 0.6, 0.62] });
  t.dispose();
}

export function satDish(w, { x, y, z, rotY = 0, r = 0.42, tilt = 0.55 }) {
  const dish = stripAttrs(new THREE.SphereGeometry(r * 1.9, 16, 8, 0, Math.PI * 2, 0, 0.42).toNonIndexed());
  dish.rotateX(Math.PI - tilt);
  w.batch.add(dish, 'plastic_crate', { matrix: xform([x, y + 0.62, z], rotY), surface: 'plastic', uvScale: 0.6, tint: [1.02, 1.0, 0.96] });
  dish.dispose();
  const arm = chamferCyl(0.022, 0.5, 6, 0.005);
  arm.rotateZ(0.5);
  w.batch.add(arm, 'metal_painted', { matrix: xform([x + 0.16, y + 0.72, z], rotY), surface: 'metal', uvScale: 0.3 });
  arm.dispose();
  const mast = chamferCyl(0.035, 0.66, 8, 0.008);
  w.batch.add(mast, 'metal_rusted', { matrix: xform([x, y + 0.33, z], rotY), surface: 'metal', uvScale: 0.3 });
  mast.dispose();
  const base = chamferBox(0.34, 0.10, 0.34, 0.024);
  w.batch.add(base, 'concrete_wall', { matrix: xform([x, y + 0.045, z], rotY), surface: 'concrete', uvScale: 0.3 });
  base.dispose();
}

export function antenna(w, { x, y, z, h = 3.2, arms = 4 }) {
  const mast = taperCyl(0.045, 0.018, h, 7);
  w.batch.add(mast, 'metal_rusted', { matrix: xform([x, y + h / 2, z], 0), surface: 'metal', uvScale: 0.4 });
  mast.dispose();
  for (let i = 0; i < arms; i++) {
    const ay = y + h * (0.45 + (i / arms) * 0.5);
    const len = 0.9 - i * 0.13;
    const g = chamferBox(len, 0.02, 0.02, 0.005);
    w.batch.add(g, 'metal_rusted', { matrix: xform([x, ay, z], i * 0.4), surface: 'metal', uvScale: 0.3 });
    g.dispose();
  }
  const base = chamferBox(0.32, 0.17, 0.32, 0.03);
  w.batch.add(base, 'concrete_wall', { matrix: xform([x, y + 0.08, z], 0), surface: 'concrete', uvScale: 0.3 });
  base.dispose();
}

/**
 * Roof water tank on a welded stand.
 *
 * The stand is the part that goes wrong: four 7 cm sticks ending exactly on the
 * deck plane read as not touching it at any distance, because there is no foot,
 * no skid and nothing to catch a contact shadow. So the legs now run 5 cm INTO
 * the deck, sit on splayed foot plates, and are tied together by a skid frame at
 * ankle height. `mat` exists because every tank on the map being the same
 * saturated blue polyethylene is its own defect.
 */
export function waterTank(w, { x, y, z, r = 0.55, h = 0.95, rotY = 0, mat = 'plastic_crate', tint = null, stand = 0.47, solid = true }) {
  const surf = mat === 'plastic_crate' ? 'plastic' : 'metal';
  /*
   * `stand` is the leg height. It is a parameter because the roofline pass needs
   * tanks that clear a 0.6–1.6 m parapet: a tank behind a parapet contributes
   * nothing to the silhouette, which is exactly why every roof in the build read
   * as bare from the street while being covered in furniture from above.
   */
  const legH = stand + 0.05;            // top of the stand = y + stand
  const deck = y + stand;
  const body = chamferCyl(r, h, 14, 0.04);
  w.batch.add(body, mat, { matrix: xform([x, deck + h / 2, z], rotY), surface: surf, uvScale: 0.7, tint: tint || [1.0, 0.97, 0.9] });
  body.dispose();
  const lid = chamferCyl(r * 0.42, 0.08, 10, 0.014);
  w.batch.add(lid, mat, { matrix: xform([x, deck + h + 0.04, z], rotY), surface: surf, uvScale: 0.3, tint });
  lid.dispose();
  const foot = [];
  for (let i = 0; i < 4; i++) {
    const a = rotY + i * Math.PI / 2 + Math.PI / 4;
    const px = x + Math.cos(a) * (r - 0.1), pz = z + Math.sin(a) * (r - 0.1);
    foot.push([px, pz]);
    // -0.05 .. +0.42: the leg starts below the deck, so no float is possible
    const leg = chamferBox(0.075, legH, 0.075, 0.014);
    w.batch.add(leg, 'metal_rusted', { matrix: xform([px, y - 0.05 + legH / 2, pz], 0), surface: 'metal', uvScale: 0.25 });
    leg.dispose();
    const pad = chamferBox(0.19, 0.03, 0.19, 0.01);
    w.batch.add(pad, 'metal_rusted', { matrix: xform([px, y + 0.015, pz], a), surface: 'metal', uvScale: 0.2, tint: [0.86, 0.82, 0.78] });
    pad.dispose();
  }
  // skid frame tying the four legs together just above the deck
  for (let i = 0; i < 4; i++) {
    const a = foot[i], b = foot[(i + 1) % 4];
    const rail = chamferBox(Math.hypot(b[0] - a[0], b[1] - a[1]), 0.05, 0.05, 0.014);
    w.batch.add(rail, 'metal_rusted', {
      matrix: alignX([a[0], y + 0.14, a[1]], [b[0], y + 0.14, b[1]]), surface: 'metal', uvScale: 0.25,
    });
    rail.dispose();
  }
  // a tall stand needs a second brace ring or it reads as four unbraced sticks
  if (stand > 0.7) {
    for (let i = 0; i < 4; i++) {
      const a = foot[i], b = foot[(i + 1) % 4];
      const rail = chamferBox(Math.hypot(b[0] - a[0], b[1] - a[1]), 0.045, 0.045, 0.012);
      w.batch.add(rail, 'metal_rusted', {
        matrix: alignX([a[0], deck - 0.08, a[1]], [b[0], deck - 0.08, b[1]]), surface: 'metal', uvScale: 0.25,
      });
      rail.dispose();
    }
  }
  // deck plate: the tank's own footprint, so the contact join is never a gap
  const plate = chamferBox(r * 1.95, 0.025, r * 1.95, 0.008);
  w.batch.add(plate, 'concrete_wall', { matrix: xform([x, y + 0.012, z], rotY), surface: 'concrete', uvScale: 0.5, cast: false, tint: [0.78, 0.76, 0.73] });
  plate.dispose();
  const pipe = tube([[x + r * 0.7, deck, z], [x + r * 0.85, y + 0.1, z], [x + r * 0.9, y + 0.02, z + 0.25]], 0.028, 6);
  w.batch.add(pipe, 'metal_rusted', { surface: 'metal', uvScale: 0.3 });
  pipe.dispose();
  if (solid) w.blocker(x, deck + h / 2, z, r * 2, h + stand, r * 2, 0, 'metal', false);
}

export function chimneyStack(w, { x, y, z, h = 1.5, wdt = 0.6, mat = 'concrete_wall', solid = true }) {
  const g = chamferBox(wdt, h, wdt, 0.035);
  w.batch.add(g, mat, { matrix: xform([x, y + h / 2, z], 0), surface: 'concrete', uvScale: 0.8, tint: [1.02, 0.96, 0.88] });
  g.dispose();
  const cap = chamferBox(wdt + 0.16, 0.10, wdt + 0.16, 0.028);
  w.batch.add(cap, 'concrete_wall', { matrix: xform([x, y + h + 0.045, z], 0), surface: 'concrete', uvScale: 0.4 });
  cap.dispose();
  const flue = chamferCyl(0.09, 0.4, 8, 0.015);
  w.batch.add(flue, 'metal_rusted', { matrix: xform([x, y + h + 0.28, z], 0), surface: 'metal', uvScale: 0.25 });
  flue.dispose();
  if (solid) w.blocker(x, y + h / 2, z, wdt, h, wdt, 0, 'concrete', false);
}

/** Squat mushroom vent / extract cowl — the commonest thing on a flat roof. */
export function roofVent(w, { x, y, z, rotY = 0, h = 0.5, r = 0.17 }) {
  const kerb = chamferBox(r * 3.0, 0.16, r * 3.0, 0.028);
  w.batch.add(kerb, 'concrete_wall', { matrix: xform([x, y + 0.08, z], rotY), surface: 'concrete', uvScale: 0.4, tint: [0.94, 0.92, 0.88] });
  kerb.dispose();
  const stack = chamferCyl(r, h, 10, 0.02);
  w.batch.add(stack, 'metal_painted', { matrix: xform([x, y + 0.16 + h / 2, z], rotY), surface: 'metal', uvScale: 0.3, tint: [0.9, 0.9, 0.88] });
  stack.dispose();
  const cowl = taperCyl(r * 1.9, r * 0.5, 0.20, 10);
  w.batch.add(cowl, 'metal_rusted', { matrix: xform([x, y + 0.16 + h + 0.08, z], rotY), surface: 'metal', uvScale: 0.3 });
  cowl.dispose();
}

/** A run of rectangular ducting on sleeper feet. */
export function roofDuct(w, { x, y, z, rotY = 0, len = 3.2, wdt = 0.42, h = 0.34 }) {
  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  const at = (u, v) => [x + cos * u + sin * v, z - sin * u + cos * v];
  // `metal_painted`, not `corrugated_metal`: the only other corrugated piece in
  // the map is the shutter box, which is a non-caster, so a casting duct would
  // open a second corrugated batch group — one whole draw call for one prop.
  const body = chamferBox(len, h, wdt, 0.03);
  w.batch.add(body, 'metal_painted', { matrix: xform([x, y + 0.24 + h / 2, z], rotY), surface: 'metal', uvScale: 0.45, tint: [0.86, 0.88, 0.9] });
  body.dispose();
  // flange rings so it is not one extruded bar
  for (const t of [-0.28, 0.06, 0.34]) {
    const f = at(len * t, 0);
    const g = chamferBox(0.06, h + 0.07, wdt + 0.07, 0.016);
    w.batch.add(g, 'metal_painted', { matrix: xform([f[0], y + 0.24 + h / 2, f[1]], rotY), surface: 'metal', uvScale: 0.25, tint: [0.8, 0.8, 0.78] });
    g.dispose();
  }
  for (const t of [-0.36, 0, 0.36]) {
    const f = at(len * t, 0);
    const leg = chamferBox(0.14, 0.24, wdt + 0.12, 0.022);
    w.batch.add(leg, 'concrete_wall', { matrix: xform([f[0], y + 0.12, f[1]], rotY), surface: 'concrete', uvScale: 0.3, tint: [0.86, 0.84, 0.8] });
    leg.dispose();
  }
}

/** Raised roof-access hatch housing. */
export function roofHatch(w, { x, y, z, rotY = 0, wdt = 1.0, dep = 0.9, h = 0.62 }) {
  const g = chamferBox(wdt, h, dep, 0.035);
  w.batch.add(g, 'plaster_wall', { matrix: xform([x, y + h / 2, z], rotY), surface: 'concrete', uvScale: 0.7, tint: [1.0, 0.97, 0.92] });
  g.dispose();
  const lid = chamferBox(wdt + 0.14, 0.07, dep + 0.14, 0.022);
  w.batch.add(lid, 'metal_rusted', { matrix: xform([x, y + h + 0.035, z], [0.10, rotY, 0]), surface: 'metal', uvScale: 0.4 });
  lid.dispose();
  w.blocker(x, y + h / 2, z, wdt, h, dep, rotY, 'concrete', false);
}

/**
 * The roof *deck* treatment: gravel ballast, tar seams, ponding stains and
 * silver patch repairs. Every roof in the build was a bare grey slab; this is
 * the cheapest possible cure, because every piece lands in a batch group that
 * already exists, so it costs triangles and not a single draw call.
 */
export function roofSurface(w, { x0, z0, x1, z1, y, patches = 5 }) {
  const rng = w.rng;
  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  const wdt = X1 - X0, dep = Z1 - Z0;
  if (wdt < 1.6 || dep < 1.6) return;
  const n = Math.max(2, Math.round(patches * Math.min(3, (wdt * dep) / 90)));
  for (let i = 0; i < n; i++) {
    const k = rng.range(0.76, 1.06);
    driftPatch(w, {
      x: rng.range(X0 + 0.7, X1 - 0.7), z: rng.range(Z0 + 0.7, Z1 - 0.7),
      rx: rng.range(0.8, Math.max(1.0, wdt * 0.22)), rz: rng.range(0.8, Math.max(1.0, dep * 0.22)),
      rot: rng.range(0, Math.PI), y: y + 0.012, mat: 'asphalt', surface: 'concrete',
      tint: [1.18 * k, 1.06 * k, 0.86 * k],
    });
  }
  for (let i = 0; i < Math.max(1, Math.round(n * 0.5)); i++) {
    driftPatch(w, {
      x: rng.range(X0 + 0.7, X1 - 0.7), z: rng.range(Z0 + 0.7, Z1 - 0.7),
      rx: rng.range(0.6, 1.7), rz: rng.range(0.6, 1.6), rot: rng.range(0, Math.PI),
      y: y + 0.02, mat: 'gravel', surface: 'dirt', tint: [0.82, 0.80, 0.74],
    });
  }
  crackScatter(w, { x0: X0 + 0.5, z0: Z0 + 0.5, x1: X1 - 0.5, z1: Z1 - 0.5, count: Math.max(3, Math.round(n * 1.4)), y: y + 0.016, mat: 'asphalt' });
  // ponding stains where the fall is wrong
  for (let i = 0; i < Math.max(1, Math.round(n * 0.4)); i++) {
    driftPatch(w, {
      x: rng.range(X0 + 0.9, X1 - 0.9), z: rng.range(Z0 + 0.9, Z1 - 0.9),
      rx: rng.range(0.8, 1.9), rz: rng.range(0.8, 1.9), rot: rng.range(0, Math.PI),
      y: y + 0.024, mat: 'concrete_floor', surface: 'concrete', seg: 22,
      tint: [0.60, 0.62, 0.66],
    });
  }
  // silver-painted patch repairs
  for (let i = 0; i < Math.max(1, Math.round(n * 0.4)); i++) {
    const g = quadXZ(rng.range(0.9, 2.2), rng.range(0.7, 1.7));
    w.batch.add(g, 'concrete_floor', {
      matrix: xform([rng.range(X0 + 0.9, X1 - 0.9), y + 0.03, rng.range(Z0 + 0.9, Z1 - 0.9)], rng.range(-0.3, 0.3)),
      surface: 'concrete', uvScale: 1.1, cast: false, tint: [1.22, 1.20, 1.16],
    });
    g.dispose();
  }
}

/** Scatter a believable mix of roof furniture over a roof rectangle. */
export function roofClutter(w, { x0, z0, x1, z1, y, density = 1, seedKinds = null }) {
  const rng = w.rng;
  const kinds = seedKinds || [
    'ac', 'ac', 'dish', 'tank', 'antenna', 'chimney', 'crateStack', 'pipes',
    'vent', 'vent', 'duct', 'hatch', 'debris',
  ];
  const area = Math.abs((x1 - x0) * (z1 - z0));
  const n = Math.max(3, Math.round(area * 0.05 * density));
  const inset = 1.0;
  for (let i = 0; i < n; i++) {
    const x = rng.range(Math.min(x0, x1) + inset, Math.max(x0, x1) - inset);
    const z = rng.range(Math.min(z0, z1) + inset, Math.max(z0, z1) - inset);
    const rotY = rng.range(0, Math.PI * 2);
    switch (rng.pick(kinds)) {
      case 'ac': acUnit(w, { x, y, z, rotY, scale: rng.range(0.85, 1.15) }); break;
      case 'dish': satDish(w, { x, y, z, rotY, tilt: rng.range(0.4, 0.75) }); break;
      case 'tank': {
        // one blue polyethylene tank per roof is characterful; every tank on the
        // map being the same blue is the "identical instanced props" note.
        const r2 = rng();
        const mat = r2 < 0.45 ? 'plastic_crate' : (r2 < 0.78 ? 'metal_painted' : 'metal_rusted');
        const tint = mat === 'plastic_crate'
          ? [rng.range(0.9, 1.1), rng.range(0.92, 1.05), rng.range(0.85, 1.0)]
          : [rng.range(0.86, 1.12), rng.range(0.86, 1.08), rng.range(0.82, 1.02)];
        waterTank(w, { x, y, z, rotY, r: rng.range(0.45, 0.65), h: rng.range(0.8, 1.1), mat, tint });
        break;
      }
      case 'antenna': antenna(w, { x, y, z, h: rng.range(2.2, 4.6), arms: rng.int(3, 5) }); break;
      case 'chimney': chimneyStack(w, { x, y, z, h: rng.range(0.9, 1.8), wdt: rng.range(0.5, 0.75) }); break;
      case 'vent': roofVent(w, { x, y, z, rotY, h: rng.range(0.35, 0.75), r: rng.range(0.13, 0.22) }); break;
      case 'duct': roofDuct(w, { x, y, z, rotY, len: rng.range(2.0, 4.0), wdt: rng.range(0.34, 0.5), h: rng.range(0.28, 0.42) }); break;
      case 'hatch': roofHatch(w, { x, y, z, rotY, wdt: rng.range(0.85, 1.2), dep: rng.range(0.8, 1.1), h: rng.range(0.5, 0.8) }); break;
      case 'debris': {
        // knocked-about blockwork and a tipped drum: the roof has been lived on
        for (let k = 0; k < rng.int(3, 6); k++) {
          const s = rng.range(0.14, 0.32);
          const g = chamferBox(s, s * rng.range(0.3, 0.6), s * rng.range(0.5, 1.1), 0.022);
          const v = rng.range(0.62, 0.98);
          w.batch.add(g, 'concrete_wall', {
            matrix: xform([x + rng.range(-0.7, 0.7), y + s * 0.18, z + rng.range(-0.7, 0.7)],
              [rng.range(-0.16, 0.16), rng.range(0, 3.1), rng.range(-0.16, 0.16)]),
            surface: 'concrete', uvScale: 0.5, tint: [v * 1.04, v, v * 0.93],
          });
          g.dispose();
        }
        break;
      }
      case 'crateStack': {
        const nn = rng.int(1, 3);
        for (let k = 0; k < nn; k++) {
          const g = chamferBox(0.62, 0.42, 0.5, 0.02);
          w.batch.add(g, 'wood_crate', {
            matrix: xform([x + rng.range(-0.1, 0.1), y + 0.21 + k * 0.43, z + rng.range(-0.1, 0.1)], rotY + rng.range(-0.3, 0.3)),
            surface: 'wood', uvScale: 0.5,
          });
          g.dispose();
        }
        break;
      }
      default: {
        // a low run of service pipes
        const len = rng.range(1.4, 3.2);
        const a = [x - len / 2, y + 0.16, z], b = [x + len / 2, y + 0.16, z];
        for (const off of [-0.09, 0.09]) {
          const t = tube([[a[0], a[1], a[2] + off], [b[0], b[1], b[2] + off]], 0.045, 6);
          w.batch.add(t, 'metal_rusted', { surface: 'metal', uvScale: 0.3 });
          t.dispose();
        }
        for (let k = 0; k <= 2; k++) {
          const px = a[0] + (b[0] - a[0]) * (k / 2);
          const s = chamferBox(0.1, 0.16, 0.3, 0.012);
          w.batch.add(s, 'metal_rusted', { matrix: xform([px, y + 0.08, z], 0), surface: 'metal', uvScale: 0.25 });
          s.dispose();
        }
      }
    }
  }
}

/* ------------------------------------------------------------------ */
/* wires, laundry, pipes                                               */
/* ------------------------------------------------------------------ */

export function wire(w, a, b, sag = 0.7, r = 0.015, mat = 'metal_rusted') {
  const pts = catenary(a, b, sag, 12);
  const g = tube(pts, r, 5);
  w.batch.add(g, mat, { surface: 'metal', uvScale: 0.2, cast: false, tint: [0.30, 0.29, 0.28] });
  g.dispose();
}

export function wireBundle(w, a, b, { count = 3, sag = 0.7, spread = 0.12 }) {
  for (let i = 0; i < count; i++) {
    const o = (i - (count - 1) / 2) * spread;
    wire(w, [a[0], a[1] + o * 0.4, a[2] + o], [b[0], b[1] + o * 0.4, b[2] + o], sag + i * 0.06);
  }
}

/** Washing hung out over the street — the cheapest silhouette in the kit. */
const WASHING = [
  [2.30, 2.20, 2.05],   // white sheet
  [2.10, 2.05, 1.95],   // off-white
  [1.55, 1.70, 2.15],   // pale blue
  [2.20, 1.45, 1.05],   // terracotta
  [1.30, 1.55, 1.35],   // faded green
  [2.15, 1.90, 1.20],   // ochre
];
export function laundryLine(w, a, b, { items = 5, sag = 0.5 } = {}) {
  const rng = w.rng;
  wire(w, a, b, sag, 0.014, 'metal_rusted');
  const pts = catenary(a, b, sag, items + 1);
  for (let i = 1; i <= items; i++) {
    const p = pts[i];
    const hw = rng.range(0.28, 0.55), hh = rng.range(0.45, 0.95);
    // real thickness, not a zero-width card: a hanging sheet seen edge-on used
    // to vanish to a razor line with a hard 90 deg silhouette
    const g = chamferBox(hw, hh, 0.022, 0.008);
    const rot = Math.atan2(-(b[2] - a[2]), b[0] - a[0]);
    const c = WASHING[rng.int(0, WASHING.length - 1)];
    const k = rng.range(0.82, 1.1);
    w.batch.add(g, 'canvas', {
      matrix: xform([p.x, p.y - hh / 2 - 0.03, p.z], rot + rng.range(-0.12, 0.12)),
      surface: 'fabric', uvScale: 0.6, cast: true,
      tint: [c[0] * k, c[1] * k, c[2] * k],
    });
    g.dispose();
  }
}

/** Drainpipe running down a wall face, with hopper head and a kicked-out shoe. */
export function drainpipe(w, { x, z, y0, y1, rotY = 0, mat = 'metal_rusted' }) {
  const g = chamferCyl(0.055, y1 - y0, 8, 0.01);
  w.batch.add(g, mat, { matrix: xform([x, (y0 + y1) / 2, z], 0), surface: 'metal', uvScale: 0.35 });
  g.dispose();
  const hop = chamferBox(0.24, 0.26, 0.2, 0.028);
  w.batch.add(hop, mat, { matrix: xform([x, y1 - 0.1, z], rotY), surface: 'metal', uvScale: 0.25 });
  hop.dispose();
  const n = Math.max(1, Math.floor((y1 - y0) / 1.6));
  for (let i = 1; i <= n; i++) {
    const b = chamferBox(0.13, 0.05, 0.16, 0.008);
    w.batch.add(b, mat, { matrix: xform([x, y0 + (i * (y1 - y0)) / (n + 1), z], rotY), surface: 'metal', uvScale: 0.2 });
    b.dispose();
  }
  const shoe = tube([[x, y0 + 0.4, z], [x, y0 + 0.16, z], [x + Math.sin(rotY) * 0.28, y0 + 0.09, z + Math.cos(rotY) * 0.28]], 0.055, 6);
  w.batch.add(shoe, mat, { surface: 'metal', uvScale: 0.25 });
  shoe.dispose();
  // rust/damp stain plate on the wall behind
  const st = quadXY(0.42, (y1 - y0) * 0.7);
  w.batch.add(st, 'concrete_wall', {
    matrix: xform([x - Math.sin(rotY) * 0.085, y0 + (y1 - y0) * 0.35, z - Math.cos(rotY) * 0.085], rotY),
    surface: 'concrete', uvScale: 0.7, tint: [0.66, 0.6, 0.53], cast: false,
  });
  st.dispose();
}

/** Surface-run electrical conduit with junction boxes. */
export function conduit(w, pts, { r = 0.03, boxes = 1 } = {}) {
  const g = tube(pts, r, 5);
  w.batch.add(g, 'metal_painted', { surface: 'metal', uvScale: 0.25, tint: [0.8, 0.8, 0.78] });
  g.dispose();
  for (let i = 0; i < boxes; i++) {
    const p = pts[Math.min(pts.length - 1, Math.floor(((i + 0.5) / boxes) * pts.length))];
    const b = chamferBox(0.17, 0.21, 0.10, 0.022);
    w.batch.add(b, 'metal_painted', { matrix: xform([p[0] ?? p.x, p[1] ?? p.y, p[2] ?? p.z], 0), surface: 'metal', uvScale: 0.2 });
    b.dispose();
  }
}

/* ------------------------------------------------------------------ */
/* awnings, signage, shutters                                          */
/* ------------------------------------------------------------------ */

/**
 * Sloped fabric awning on a steel frame, projecting from a wall.
 *
 * `canvas`, not `fabric_tarp`: the tarp material is a dark olive army sheet
 * (linear albedo 0.02–0.07) and every awning, rug and washing line dressed with
 * it rendered as a black rectangle stuck to a sunlit wall. Canvas is ~3x
 * brighter and takes a tint above 1.0 — see AO_PROFILES.canvas in Geo.js.
 */
export function awning(w, {
  x, z, y, rotY = 0, width = 3.0, depth = 1.5, drop = 0.5,
  mat = 'canvas', tint = null, scallop = true, poles = false, poleBase = 0,
  stays = true, register = true,
}) {
  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  const at = (u, v) => [x + cos * u + sin * v, z - sin * u + cos * v];
  // canopy: a thin slab tilted down and away. 8 cm thick with a 3.5 cm chamfer:
  // a 5 cm slab clamps its own bevel down to ~1 cm (chamferBox caps at
  // half-extent x 0.45) and then reads as an unbevelled plate at 3 m, which is
  // exactly the note the market-stall canopies picked up.
  const canopy = chamferBox(width, 0.08, Math.hypot(depth, drop), 0.035);
  const pitch = Math.atan2(drop, depth);
  const mid = at(0, depth / 2);
  const m = new THREE.Matrix4();
  m.compose(
    new THREE.Vector3(mid[0], y - drop / 2, mid[1]),
    new THREE.Quaternion().setFromEuler(new THREE.Euler(pitch, rotY, 0, 'YXZ')),
    new THREE.Vector3(1, 1, 1));
  w.batch.add(canopy, mat, { matrix: m, surface: 'fabric', uvScale: 0.9, tint });
  canopy.dispose();
  // back rail: the piece that says "this is bolted to something". Without it the
  // canopy's inner edge simply stops in mid-air.
  {
    const a = at(-width / 2 - 0.05, 0.03), b = at(width / 2 + 0.05, 0.03);
    const bar = chamferBox(width + 0.1, 0.09, 0.09, 0.02);
    w.batch.add(bar, 'metal_rusted', { matrix: alignX([a[0], y + 0.045, a[1]], [b[0], y + 0.045, b[1]]), surface: 'metal', uvScale: 0.3 });
    bar.dispose();
  }
  // frame: two rafters + a front rail
  for (const s of [-1, 1]) {
    const a = [...at(s * (width / 2 - 0.08), 0.02), 0];
    const b = [...at(s * (width / 2 - 0.08), depth), 0];
    const A = [a[0], y + 0.02, a[1]], B = [b[0], y - drop, b[1]];
    const len = segLength(A, B);
    const bar = chamferBox(len, 0.05, 0.05, 0.012);
    w.batch.add(bar, 'metal_rusted', { matrix: alignX(A, B), surface: 'metal', uvScale: 0.3 });
    bar.dispose();
  }
  {
    const a = at(-width / 2, depth), b = at(width / 2, depth);
    const bar = chamferBox(width, 0.055, 0.055, 0.014);
    w.batch.add(bar, 'metal_rusted', { matrix: alignX([a[0], y - drop, a[1]], [b[0], y - drop, b[1]]), surface: 'metal', uvScale: 0.3 });
    bar.dispose();
  }
  if (scallop) {
    const val = chamferBox(width, 0.30, 0.035, 0.014);
    const p = at(0, depth);
    w.batch.add(val, mat, { matrix: xform([p[0], y - drop - 0.15, p[1]], rotY), surface: 'fabric', uvScale: 0.5, tint });
    val.dispose();
  }
  if (poles) {
    // poles stand on whatever deck the awning is over — a balcony slab, a roof,
    // the pavement — not always on y = 0.
    const h = y - drop - poleBase;
    for (const s of [-1, 1]) {
      const p = at(s * (width / 2 - 0.08), depth);
      const pole = chamferCyl(0.038, h, 8, 0.01);
      w.batch.add(pole, 'metal_rusted', { matrix: xform([p[0], poleBase + h / 2, p[1]], 0), surface: 'metal', uvScale: 0.3 });
      pole.dispose();
      // foot plate, so the pole meets the deck instead of ending in it
      const foot = chamferBox(0.16, 0.035, 0.16, 0.012);
      w.batch.add(foot, 'metal_rusted', { matrix: xform([p[0], poleBase + 0.018, p[1]], 0), surface: 'metal', uvScale: 0.2, tint: [0.86, 0.82, 0.78] });
      foot.dispose();
      w.blocker(p[0], poleBase + h / 2, p[1], 0.1, h, 0.1, 0, 'metal', false);
    }
  } else if (stays) {
    // a cantilevered awning is held by diagonal tie-rods back to the wall. They
    // are the difference between "projecting" and "floating".
    for (const s of [-1, 1]) {
      const f = at(s * (width / 2 - 0.10), depth);
      const b = at(s * (width / 2 - 0.10), -0.02);
      const A = [f[0], y - drop + 0.02, f[1]];
      const B = [b[0], y + Math.max(0.55, drop + 0.35), b[1]];
      const rod = chamferBox(segLength(A, B), 0.032, 0.032, 0.008);
      w.batch.add(rod, 'metal_rusted', { matrix: alignX(A, B), surface: 'metal', uvScale: 0.25 });
      rod.dispose();
      // wall plate the stay lands on
      const pl = chamferBox(0.14, 0.14, 0.05, 0.015);
      w.batch.add(pl, 'metal_rusted', { matrix: xform([B[0], B[1], B[2]], rotY), surface: 'metal', uvScale: 0.2, tint: [0.8, 0.76, 0.72] });
      pl.dispose();
    }
  }
  if (register) {
    const c0 = at(-width / 2, 0), c1 = at(width / 2, 0);
    const c2 = at(width / 2, depth), c3 = at(-width / 2, depth);
    w.overhead('awning', [
      [c0[0], y - 0.06, c0[1]], [c1[0], y - 0.06, c1[1]],
      [c2[0], y - drop - 0.06, c2[1]], [c3[0], y - drop - 0.06, c3[1]],
    ], {
      // with poles the front corners are held; without them the wall is, and a
      // stay-braced awning may cantilever its full depth
      reach: poles ? 1.9 : Math.max(1.9, depth + 0.4),
      root: poles ? null : { p: [x, y - 0.12, z], dir: [-sin, 0, -cos] },
    });
  }
}

/** Shop sign board (emissive practical for the lighting team). */
export function sign(w, { x, y, z, rotY = 0, width = 2.4, height = 0.6, mat = 'metal_painted', lit = true, color = 0xffd08a }) {
  const board = chamferBox(width, height, 0.10, 0.03);
  w.batch.add(board, mat, { matrix: xform([x, y, z], rotY), surface: 'metal', uvScale: 0.6, tint: [1.02, 0.98, 0.9] });
  board.dispose();
  const frame = chamferBox(width + 0.12, height + 0.12, 0.06, 0.022);
  w.batch.add(frame, 'metal_rusted', { matrix: xform([x, y, z - 0.03], rotY), surface: 'metal', uvScale: 0.4 });
  frame.dispose();
  for (const s of [-1, 1]) {
    const bx = x + Math.cos(rotY) * s * (width / 2 - 0.15);
    const bz = z - Math.sin(rotY) * s * (width / 2 - 0.15);
    const br = chamferBox(0.05, 0.05, 0.3, 0.01);
    w.batch.add(br, 'metal_rusted', { matrix: xform([bx, y + height / 2 + 0.1, bz - 0.14], rotY), surface: 'metal', uvScale: 0.2 });
    br.dispose();
  }
  if (lit) {
    w.light([x + Math.sin(rotY) * 0.5, y, z + Math.cos(rotY) * 0.5], color, 3.2, 5.5, 'point', { role: 'signage' });
  }
}

/** Hanging bulb on a flex — an interior practical. */
export function hangingBulb(w, { x, y, z, drop = 0.8, shade = true, color = 0xffcf9c, intensity = 4.5, radius = 7 }) {
  const flex = chamferCyl(0.008, drop, 5, 0.002);
  w.batch.add(flex, 'metal_rusted', { matrix: xform([x, y - drop / 2, z], 0), surface: 'metal', uvScale: 0.2, cast: false });
  flex.dispose();
  if (shade) {
    const sh = taperCyl(0.22, 0.06, 0.2, 12);
    w.batch.add(sh, 'metal_painted', { matrix: xform([x, y - drop - 0.06, z], 0), surface: 'metal', uvScale: 0.3, tint: [1.05, 1.0, 0.94] });
    sh.dispose();
  }
  const bulb = chamferCyl(0.045, 0.09, 8, 0.02);
  w.batch.add(bulb, 'glass', { matrix: xform([x, y - drop - 0.18, z], 0), surface: 'glass', uvScale: 0.2, cast: false });
  bulb.dispose();
  w.light([x, y - drop - 0.18, z], color, intensity, radius, 'point', { role: 'practical' });
}

/** Bracket wall lamp — alley practicals. */
export function wallLamp(w, { x, y, z, rotY = 0, color = 0xffc98c, intensity = 3.5, radius = 8 }) {
  const arm = chamferBox(0.42, 0.05, 0.05, 0.012);
  const px = x + Math.sin(rotY) * 0.21, pz = z + Math.cos(rotY) * 0.21;
  w.batch.add(arm, 'metal_rusted', { matrix: xform([px, y, pz], rotY + Math.PI / 2), surface: 'metal', uvScale: 0.25 });
  arm.dispose();
  const hx = x + Math.sin(rotY) * 0.42, hz = z + Math.cos(rotY) * 0.42;
  const hood = taperCyl(0.2, 0.07, 0.16, 10);
  w.batch.add(hood, 'metal_painted', { matrix: xform([hx, y - 0.08, hz], 0), surface: 'metal', uvScale: 0.25 });
  hood.dispose();
  const g = chamferCyl(0.05, 0.08, 8, 0.02);
  w.batch.add(g, 'glass', { matrix: xform([hx, y - 0.2, hz], 0), surface: 'glass', uvScale: 0.2, cast: false });
  g.dispose();
  w.light([hx, y - 0.22, hz], color, intensity, radius, 'point', { role: 'practical' });
}

/**
 * Rebar sticking out of broken concrete.
 *
 * `y` is the surface it grows OUT of, and the bar starts 0.14 m below it: a bar
 * whose lowest vertex sits exactly on — or, as before, 0.30 m above — the deck
 * reads as a rust-coloured stick balanced in mid-air, which is precisely the
 * "ungrounded poles stabbing through the plaza" note on `level_rooftop`. It also
 * casts now; without a shadow the base has nothing to sit in.
 */
export function rebar(w, { x, y, z, count = 4, spread = 0.3, len = 0.8, collar = true }) {
  const rng = w.rng;
  for (let i = 0; i < count; i++) {
    const ax = x + rng.range(-spread, spread), az = z + rng.range(-spread, spread);
    const a = [ax, y - 0.14, az];
    const b = [ax + rng.range(-0.25, 0.25), y + len * rng.range(0.5, 1.2), az + rng.range(-0.25, 0.25)];
    const g = tube([a, [(a[0] + b[0]) / 2 + rng.range(-0.08, 0.08), (a[1] + b[1]) / 2, (a[2] + b[2]) / 2], b], 0.013, 4);
    w.batch.add(g, 'metal_rusted', { surface: 'metal', uvScale: 0.2, tint: [0.66, 0.56, 0.48] });
    g.dispose();
    if (collar) {
      // the snapped concrete the bar comes out of — the contact the eye looks for
      const c = chamferBox(rng.range(0.11, 0.19), rng.range(0.05, 0.09), rng.range(0.10, 0.18), 0.018);
      w.batch.add(c, 'concrete_wall', {
        matrix: xform([ax, y + 0.02, az], [rng.range(-0.12, 0.12), rng.range(0, 3.1), rng.range(-0.12, 0.12)]),
        surface: 'concrete', uvScale: 0.4, tint: [0.72, 0.69, 0.64],
      });
      c.dispose();
    }
  }
}

/* ------------------------------------------------------------------ */
/* THE ROOFLINE PASS — silhouette break-up where geometry meets sky     */
/* ------------------------------------------------------------------ */

/*
 * Why this exists, and why `roofClutter` was not enough.
 *
 * `roofClutter` dresses the roof DECK: AC units, ducts, vents, hatches, tanks on
 * 47 cm stands. Every one of them is under 1.1 m tall and is placed at least
 * 1.6 m inboard of the parapet. A parapet is 0.6–1.6 m. So from any camera at
 * or below roof level — which is every gameplay camera in the map — the parapet
 * hides all of it, and the roof reads as a bare slab with a ruler-straight top
 * edge. That is exactly what the blind test found: `level_rooftop` (320–430)
 * is a stack of flat-topped boxes; `light_goldenhour`'s left parapet
 * (0–700, y≈115) is one straight coping with a single aerial on it.
 *
 * The reference does the opposite. `refs/mw3_06.jpg`: the tower at (1700, 300)
 * is an open lattice, floodlight brackets stick up off every gantry, four
 * parallel catenaries cross the whole sky, a mast at (1180, 100) breaks the
 * cloud. `refs/mw3_04.jpg`: the terminal roof is cut by a light mast at
 * (1160, 20) and by red truss work along its whole length.
 *
 * So this pass places things ON the parapet line, and only things TALL ENOUGH
 * TO CLEAR IT. Every piece lands in a batch group `Level.js` already creates
 * (metal_rusted, metal_painted, concrete_wall, plaster_wall, canvas,
 * wood_crate, plastic_crate, metal_grate, glass), so the whole pass costs
 * triangles and **zero draw calls**.
 */

/** Guy wires from a mast head down to the deck — the tell that a mast is real. */
function guys(w, x, y, z, headY, n = 3, r = 0.55) {
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + 0.4;
    const g = tube([[x, headY, z], [x + Math.cos(a) * r, y + 0.05, z + Math.sin(a) * r]], 0.008, 4);
    w.batch.add(g, 'metal_rusted', { surface: 'metal', uvScale: 0.2, cast: false, tint: [0.34, 0.32, 0.30] });
    g.dispose();
  }
}

/**
 * A whip mast: tapered pole, a few stub crossarms, a foot and guy wires.
 * The cheapest silhouette in the kit — 1 taper + 3 boxes + 3 hairline tubes.
 */
export function whipMast(w, { x, y, z, h = 3.2, arms = 3, rotY = 0, guyed = true }) {
  const mast = taperCyl(0.042, 0.012, h, 6);
  w.batch.add(mast, 'metal_rusted', { matrix: xform([x, y + h / 2, z], 0), surface: 'metal', uvScale: 0.4 });
  mast.dispose();
  for (let i = 0; i < arms; i++) {
    const ay = y + h * (0.38 + (i / Math.max(1, arms)) * 0.54);
    const len = 0.72 - i * 0.11;
    const g = chamferBox(len, 0.018, 0.018, 0.005);
    w.batch.add(g, 'metal_rusted', { matrix: xform([x, ay, z], rotY + i * 0.7), surface: 'metal', uvScale: 0.3, cast: false });
    g.dispose();
  }
  const base = chamferBox(0.26, 0.14, 0.26, 0.026);
  w.batch.add(base, 'concrete_wall', { matrix: xform([x, y + 0.07, z], rotY), surface: 'concrete', uvScale: 0.3, tint: [0.86, 0.84, 0.8] });
  base.dispose();
  if (guyed) guys(w, x, y, z, y + h * 0.82, 3, 0.5);
}

/**
 * A Yagi TV aerial. Nine elements on a boom is an instantly readable rooftop
 * silhouette and it reads as *open* — sky comes through it, which is the whole
 * point of the note.
 */
export function tvAerial(w, { x, y, z, rotY = 0, h = 1.9, elems = 7, span = 1.05 }) {
  const mast = chamferCyl(0.026, h, 6, 0.006);
  w.batch.add(mast, 'metal_rusted', { matrix: xform([x, y + h / 2, z], 0), surface: 'metal', uvScale: 0.3 });
  mast.dispose();
  const boomLen = span * 1.35;
  const boom = chamferBox(boomLen, 0.022, 0.022, 0.006);
  w.batch.add(boom, 'metal_rusted', { matrix: xform([x, y + h, z], rotY), surface: 'metal', uvScale: 0.25, cast: false });
  boom.dispose();
  for (let i = 0; i < elems; i++) {
    const t = i / (elems - 1);
    const u = (t - 0.5) * boomLen;
    const el = span * (1.0 - t * 0.55);
    const g = chamferBox(0.016, 0.016, el, 0.004);
    w.batch.add(g, 'metal_rusted', {
      matrix: xform([x + Math.cos(rotY) * u, y + h + 0.02, z - Math.sin(rotY) * u], rotY),
      surface: 'metal', uvScale: 0.2, cast: false,
    });
    g.dispose();
  }
  const clamp = chamferBox(0.09, 0.10, 0.09, 0.018);
  w.batch.add(clamp, 'metal_rusted', { matrix: xform([x, y + h * 0.45, z], rotY), surface: 'metal', uvScale: 0.2, cast: false });
  clamp.dispose();
  const base = chamferBox(0.2, 0.11, 0.2, 0.02);
  w.batch.add(base, 'concrete_wall', { matrix: xform([x, y + 0.055, z], rotY), surface: 'concrete', uvScale: 0.3, tint: [0.86, 0.84, 0.8] });
  base.dispose();
}

/** A satellite dish on a mast tall enough to clear a parapet. */
export function dishMast(w, { x, y, z, rotY = 0, h = 1.25, r = 0.4, tilt = 0.55, tint = null }) {
  const mast = chamferCyl(0.036, h, 8, 0.008);
  w.batch.add(mast, 'metal_rusted', { matrix: xform([x, y + h / 2, z], 0), surface: 'metal', uvScale: 0.3 });
  mast.dispose();
  const dish = stripAttrs(new THREE.SphereGeometry(r * 1.85, 14, 7, 0, Math.PI * 2, 0, 0.44).toNonIndexed());
  dish.rotateX(Math.PI - tilt);
  w.batch.add(dish, 'plastic_crate', {
    matrix: xform([x, y + h + r * 0.5, z], rotY), surface: 'plastic', uvScale: 0.6,
    tint: tint || [1.05, 1.02, 0.96],
  });
  dish.dispose();
  const arm = chamferCyl(0.018, r * 1.3, 6, 0.004);
  arm.rotateZ(0.55);
  // `metal_painted` casts here on purpose: the only other painted-metal batch
  // group in the map is a caster, and a `cast:false` variant would open a second
  // group — one whole draw call for one 4 cm feed arm.
  w.batch.add(arm, 'metal_painted', { matrix: xform([x + r * 0.4, y + h + r * 0.75, z], rotY), surface: 'metal', uvScale: 0.3 });
  arm.dispose();
  const base = chamferBox(0.28, 0.10, 0.28, 0.024);
  w.batch.add(base, 'concrete_wall', { matrix: xform([x, y + 0.05, z], rotY), surface: 'concrete', uvScale: 0.3, tint: [0.86, 0.84, 0.8] });
  base.dispose();
}

/** Tall extract / soil pipe with a cowl. */
export function ventPipe(w, { x, y, z, h = 1.7, r = 0.075, rotY = 0 }) {
  const stack = chamferCyl(r, h, 8, 0.012);
  w.batch.add(stack, 'metal_painted', { matrix: xform([x, y + h / 2, z], rotY), surface: 'metal', uvScale: 0.3, tint: [0.88, 0.88, 0.86] });
  stack.dispose();
  const cowl = taperCyl(r * 2.4, r * 0.6, 0.18, 8);
  w.batch.add(cowl, 'metal_rusted', { matrix: xform([x, y + h + 0.07, z], rotY), surface: 'metal', uvScale: 0.3 });
  cowl.dispose();
  const kerb = chamferBox(r * 4, 0.12, r * 4, 0.022);
  w.batch.add(kerb, 'concrete_wall', { matrix: xform([x, y + 0.06, z], rotY), surface: 'concrete', uvScale: 0.35, tint: [0.9, 0.88, 0.84] });
  kerb.dispose();
}

/**
 * Colours for parapet signage and painted roof panels.
 *
 * These multiply `metal_painted`'s grey-green albedo, so they are the only
 * saturated non-warm accents that can survive a sun-bleached Levantine palette.
 * `refs/mw3_04.jpg` gets orange crash barriers and blue-white terminal glazing
 * into a fundamentally warm frame; the trick is that there are FEW of them and
 * they are small — not that the palette is broad.
 */
export const ACCENT = [
  [2.55, 1.15, 0.55],   // signal orange
  [2.35, 0.85, 0.62],   // oxide red
  [0.72, 1.20, 1.85],   // faded cobalt
  [0.80, 1.35, 0.90],   // sun-rotted green
  [2.30, 1.95, 0.85],   // ochre yellow
  [1.85, 1.80, 1.70],   // dirty white
];

/**
 * A hoarding / shop board standing proud of the parapet on two legs.
 * Carries the frame's colour accents as well as its silhouette.
 */
export function parapetSign(w, { x, y, z, rotY = 0, width = 1.9, height = 0.78, lift = 0.55, tint = null }) {
  const cy = y + lift + height / 2;
  const board = chamferBox(width, height, 0.07, 0.024);
  w.batch.add(board, 'metal_painted', { matrix: xform([x, cy, z], rotY), surface: 'metal', uvScale: 0.6, tint: tint || ACCENT[0] });
  board.dispose();
  const frame = chamferBox(width + 0.10, height + 0.10, 0.045, 0.018);
  w.batch.add(frame, 'metal_rusted', { matrix: xform([x, cy, z], rotY), surface: 'metal', uvScale: 0.4, cast: false });
  frame.dispose();
  for (const s of [-1, 1]) {
    const px = x + Math.cos(rotY) * s * (width / 2 - 0.14);
    const pz = z - Math.sin(rotY) * s * (width / 2 - 0.14);
    const leg = chamferBox(0.055, lift + height * 0.6, 0.055, 0.012);
    w.batch.add(leg, 'metal_rusted', { matrix: xform([px, y + (lift + height * 0.6) / 2, pz], rotY), surface: 'metal', uvScale: 0.3 });
    leg.dispose();
    // raking stay back onto the deck: a board on two vertical sticks floats
    const A = [px, cy + height * 0.2, pz];
    const B = [px - Math.sin(rotY) * 0.55, y + 0.04, pz - Math.cos(rotY) * 0.55];
    const stay = chamferBox(segLength(A, B), 0.03, 0.03, 0.008);
    w.batch.add(stay, 'metal_rusted', { matrix: alignX(A, B), surface: 'metal', uvScale: 0.25, cast: false });
    stay.dispose();
  }
}

/**
 * A washing line strung between two poles standing on the roof — the single
 * most characterful thing on a Levantine roofline, and it puts cloth colour
 * above the skyline where the frame has nothing but grey.
 */
export function laundryPost(w, { x, y, z, rotY = 0, span = 2.8, h = 1.9, items = 4 }) {
  const rng = w.rng;
  const cos = Math.cos(rotY), sin = Math.sin(rotY);
  const ends = [];
  for (const s of [-1, 1]) {
    const px = x + cos * s * span / 2, pz = z - sin * s * span / 2;
    const hh = h * rng.range(0.9, 1.1);
    const pole = chamferCyl(0.038, hh, 6, 0.008);
    w.batch.add(pole, 'metal_rusted', { matrix: xform([px, y + hh / 2, pz], 0), surface: 'metal', uvScale: 0.3 });
    pole.dispose();
    const arm = chamferBox(0.44, 0.03, 0.03, 0.008);
    w.batch.add(arm, 'metal_rusted', { matrix: xform([px, y + hh - 0.08, pz], rotY + Math.PI / 2), surface: 'metal', uvScale: 0.2, cast: false });
    arm.dispose();
    const foot = chamferBox(0.24, 0.08, 0.24, 0.018);
    w.batch.add(foot, 'concrete_wall', { matrix: xform([px, y + 0.04, pz], 0), surface: 'concrete', uvScale: 0.3, tint: [0.86, 0.84, 0.8] });
    foot.dispose();
    ends.push([px, y + hh - 0.1, pz]);
  }
  laundryLine(w, ends[0], ends[1], { items, sag: 0.28 });
}

/** A rooftop pigeon loft / tool shed — a proper mass on the skyline. */
export function roofShed(w, { x, y, z, rotY = 0, wdt = 1.5, dep = 1.1, h = 1.35 }) {
  const legH = 0.28;
  const body = chamferBox(wdt, h, dep, 0.03);
  w.batch.add(body, 'wood_crate', { matrix: xform([x, y + legH + h / 2, z], rotY), surface: 'wood', uvScale: 0.6, tint: [0.92, 0.86, 0.76] });
  body.dispose();
  // mesh front, so the box reads as a coop and light comes through it
  const mesh = chamferBox(wdt * 0.7, h * 0.55, 0.03, 0.008);
  w.batch.add(mesh, 'metal_grate', {
    matrix: xform([x + Math.sin(rotY) * (dep / 2 + 0.02), y + legH + h * 0.55, z + Math.cos(rotY) * (dep / 2 + 0.02)], rotY),
    surface: 'metal', uvScale: 0.4, cast: false,
  });
  mesh.dispose();
  // lean-to roof, pitched, overhanging — a flat lid reads as a crate
  const roof = chamferBox(wdt + 0.22, 0.06, dep + 0.26, 0.018);
  w.batch.add(roof, 'corrugated_metal', {
    matrix: xform([x, y + legH + h + 0.06, z], [0.13, rotY, 0]), surface: 'metal', uvScale: 0.5, cast: false, tint: [0.9, 0.88, 0.84],
  });
  roof.dispose();
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    const px = x + Math.cos(rotY) * sx * (wdt / 2 - 0.09) + Math.sin(rotY) * sz * (dep / 2 - 0.09);
    const pz = z - Math.sin(rotY) * sx * (wdt / 2 - 0.09) + Math.cos(rotY) * sz * (dep / 2 - 0.09);
    const leg = chamferBox(0.09, legH, 0.09, 0.016);
    w.batch.add(leg, 'wood_plank', { matrix: xform([px, y + legH / 2, pz], 0), surface: 'wood', uvScale: 0.25 });
    leg.dispose();
  }
}

/**
 * A slender open lattice tower with X bracing — `refs/mw3_06.jpg` (1700, 300).
 * The sky reads *through* it, which no solid mass can do. Expensive enough that
 * only a handful are placed by hand where the sightlines want them.
 */
export function latticeTower(w, { x, y, z, h = 18, base = 2.4, taper = 0.42, bays = 7, rotY = 0, mat = 'metal_rusted', tint = null, far = false }) {
  /*
   * `far` drops shadow CASTING only. It must not also drop `receive`: the batch
   * key is `mat|surface|cast|recv`, so a `recv:false` variant opens a second
   * `metal_rusted` merged mesh — one whole draw call for six towers nobody can
   * reach. Measured; it was one of exactly three calls this pass leaked.
   */
  const opt = { surface: 'metal', uvScale: 0.5, tint: tint || [0.86, 0.82, 0.76], cast: !far };
  const legR = 0.075;
  const at = (t, i) => {
    const s = base * (1 - (1 - taper) * t) / 2;
    const a = rotY + i * Math.PI / 2 + Math.PI / 4;
    return [x + Math.cos(a) * s * Math.SQRT2, y + h * t, z + Math.sin(a) * s * Math.SQRT2];
  };
  for (let i = 0; i < 4; i++) {
    for (let b = 0; b < bays; b++) {
      const A = at(b / bays, i), B = at((b + 1) / bays, i);
      const g = chamferBox(segLength(A, B), legR * 2, legR * 2, 0.012);
      w.batch.add(g, mat, { matrix: alignX(A, B), ...opt });
      g.dispose();
    }
  }
  for (let b = 0; b <= bays; b++) {
    const t = b / bays;
    for (let i = 0; i < 4; i++) {
      const A = at(t, i), B = at(t, (i + 1) % 4);
      const g = chamferBox(segLength(A, B), 0.05, 0.05, 0.01);
      w.batch.add(g, mat, { matrix: alignX(A, B), ...opt, cast: false });
      g.dispose();
    }
    if (b === bays) break;
    // X bracing on all four faces — this is what makes it read as open steel
    for (let i = 0; i < 4; i++) {
      const A = at(b / bays, i), B = at((b + 1) / bays, (i + 1) % 4);
      const C = at(b / bays, (i + 1) % 4), D = at((b + 1) / bays, i);
      for (const [P, Q] of [[A, B], [C, D]]) {
        const g = chamferBox(segLength(P, Q), 0.038, 0.038, 0.009);
        w.batch.add(g, mat, { matrix: alignX(P, Q), ...opt, cast: false });
        g.dispose();
      }
    }
  }
  // head gear: a platform, a couple of dishes and a whip
  const plat = chamferBox(base * taper + 0.6, 0.07, base * taper + 0.6, 0.016);
  w.batch.add(plat, mat, { matrix: xform([x, y + h + 0.04, z], rotY), ...opt, cast: false });
  plat.dispose();
  if (!far) {
    dishMast(w, { x: x + base * taper * 0.3, y: y + h + 0.07, z, rotY: rotY + 0.7, h: 0.5, r: 0.34 });
    whipMast(w, { x: x - base * taper * 0.3, y: y + h + 0.07, z, h: h * 0.22, arms: 2, guyed: false });
  } else {
    const wm = taperCyl(0.06, 0.02, h * 0.2, 5);
    w.batch.add(wm, mat, { matrix: xform([x, y + h + h * 0.1, z], 0), ...opt, cast: false });
    wm.dispose();
  }
  return [x, y + h, z];
}

/*
 * Cable anchors. Every roofline pass that plants a service pole registers its
 * head here; `linkCables` then strings catenaries between the pairs that are
 * close enough. Cables that cross the sky between blocks are half of what makes
 * `refs/mw3_06.jpg` read as a lived-in industrial site rather than a diorama,
 * and they are the cheapest geometry in the whole build.
 */
export function cablePole(w, { x, y, z, h = 2.6, rotY = 0, arms = 2 }) {
  const pole = chamferCyl(0.062, h, 7, 0.012);
  w.batch.add(pole, 'metal_rusted', { matrix: xform([x, y + h / 2, z], 0), surface: 'metal', uvScale: 0.35 });
  pole.dispose();
  for (let i = 0; i < arms; i++) {
    const ay = y + h - 0.14 - i * 0.34;
    const g = chamferBox(0.62, 0.05, 0.05, 0.012);
    w.batch.add(g, 'metal_rusted', { matrix: xform([x, ay, z], rotY), surface: 'metal', uvScale: 0.25, cast: false });
    g.dispose();
    for (const s of [-1, 1]) {
      const ins = chamferCyl(0.038, 0.10, 6, 0.014);
      w.batch.add(ins, 'glass', {
        matrix: xform([x + Math.cos(rotY) * s * 0.28, ay + 0.09, z - Math.sin(rotY) * s * 0.28], 0),
        surface: 'glass', uvScale: 0.15, cast: false,
      });
      ins.dispose();
    }
  }
  const foot = chamferBox(0.3, 0.13, 0.3, 0.024);
  w.batch.add(foot, 'concrete_wall', { matrix: xform([x, y + 0.065, z], rotY), surface: 'concrete', uvScale: 0.3, tint: [0.86, 0.84, 0.8] });
  foot.dispose();
  (w.cableAnchors || (w.cableAnchors = [])).push([x, y + h - 0.2, z]);
}

/**
 * String catenaries between registered pole heads.
 *
 * Greedy nearest-neighbour inside [min,max] metres, each anchor used at most
 * `fan` times, so the sky gets a believable web instead of every pole wired to
 * every other. `cast:false` throughout — a 1.5 cm cable's shadow is noise.
 */
export function linkCables(w, { min = 8, max = 34, fan = 2, sag = 0.9, bundle = 2 } = {}) {
  const A = w.cableAnchors || [];
  const used = new Array(A.length).fill(0);
  const pairs = [];
  for (let i = 0; i < A.length; i++) {
    const cands = [];
    for (let j = 0; j < A.length; j++) {
      if (i === j) continue;
      const d = Math.hypot(A[i][0] - A[j][0], A[i][2] - A[j][2]);
      if (d < min || d > max) continue;
      cands.push([d, j]);
    }
    cands.sort((a, b) => a[0] - b[0]);
    for (const [, j] of cands) {
      if (used[i] >= fan) break;
      if (used[j] >= fan) continue;
      const key = i < j ? `${i},${j}` : `${j},${i}`;
      if (pairs.some((p) => p.key === key)) continue;
      pairs.push({ key, a: A[i], b: A[j] });
      used[i]++; used[j]++;
    }
  }
  for (const p of pairs) {
    const d = Math.hypot(p.a[0] - p.b[0], p.a[2] - p.b[2]);
    wireBundle(w, p.a, p.b, { count: bundle, sag: sag * (0.5 + d / 40), spread: 0.13 });
  }
  return pairs.length;
}

/**
 * THE PASS. Walk the parapet line of a roof rectangle and plant tall furniture
 * on it, so the top edge of the block is never a single straight run for more
 * than a few metres.
 *
 * `parapetH` is what the item has to clear. `solid` adds collision — only worth
 * it on the two roofs the player can actually stand on.
 */
export function rooflineClutter(w, {
  x0, z0, x1, z1, y, parapetH = 0.9, density = 1, solid = false,
  edges = 'nsew', accent = 0.22, poles = 0.28, inset = 0.42, maxItems = 14,
}) {
  const rng = w.rng;
  const X0 = Math.min(x0, x1), X1 = Math.max(x0, x1);
  const Z0 = Math.min(z0, z1), Z1 = Math.max(z0, z1);
  if (X1 - X0 < 2.2 || Z1 - Z0 < 2.2) return 0;

  const runs = [];
  if (edges.includes('n')) runs.push({ a: [X0 + 0.6, Z0 + inset], b: [X1 - 0.6, Z0 + inset], out: -Math.PI / 2 });
  if (edges.includes('s')) runs.push({ a: [X0 + 0.6, Z1 - inset], b: [X1 - 0.6, Z1 - inset], out: Math.PI / 2 });
  if (edges.includes('w')) runs.push({ a: [X0 + inset, Z0 + 0.9], b: [X0 + inset, Z1 - 0.9], out: Math.PI });
  if (edges.includes('e')) runs.push({ a: [X1 - inset, Z0 + 0.9], b: [X1 - inset, Z1 - 0.9], out: 0 });

  let placed = 0;
  /*
   * Budget PER EDGE, not per building. A single global cap is spent entirely on
   * whichever edge the loop reaches first, which leaves the other three bare —
   * and the bare one is as likely as not to be the one the camera is looking at.
   */
  const perRun = Math.max(2, Math.ceil(maxItems / Math.max(1, runs.length)));
  for (const run of runs) {
    const L = Math.hypot(run.b[0] - run.a[0], run.b[1] - run.a[1]);
    if (L < 1.6) continue;
    let onRun = 0;
    let t = rng.range(0.6, 2.4);
    while (t < L - 0.6 && onRun < perRun) {
      const u = t / L;
      const px = run.a[0] + (run.b[0] - run.a[0]) * u;
      const pz = run.a[1] + (run.b[1] - run.a[1]) * u;
      const face = run.out + rng.range(-0.35, 0.35);
      // clearance: the item's own bulk must beat the parapet or it is invisible
      const clear = parapetH + rng.range(0.25, 1.1);
      const r = rng();
      if (r < poles) {
        cablePole(w, { x: px, y, z: pz, h: clear + rng.range(0.5, 1.6), rotY: face + Math.PI / 2, arms: rng.int(1, 2) });
      } else if (r < poles + accent) {
        const c = ACCENT[rng.int(0, ACCENT.length - 1)];
        const k = rng.range(0.8, 1.15);
        parapetSign(w, {
          x: px, y, z: pz, rotY: face + Math.PI / 2,
          width: rng.range(1.3, 2.8), height: rng.range(0.5, 0.95),
          lift: clear * rng.range(0.55, 0.95),
          tint: [c[0] * k, c[1] * k, c[2] * k],
        });
      } else {
        switch (rng.int(0, 8)) {
          case 0: whipMast(w, { x: px, y, z: pz, h: clear + rng.range(0.8, 3.4), arms: rng.int(2, 4), rotY: face }); break;
          case 1: tvAerial(w, { x: px, y, z: pz, rotY: face + rng.range(-0.6, 0.6), h: clear + rng.range(0.5, 1.6), elems: rng.int(5, 9), span: rng.range(0.8, 1.25) }); break;
          case 2: dishMast(w, { x: px, y, z: pz, rotY: face + rng.range(-0.5, 0.5), h: clear + rng.range(0.1, 0.7), r: rng.range(0.32, 0.5), tilt: rng.range(0.4, 0.75) }); break;
          case 3: {
            const r2 = rng();
            const mat = r2 < 0.42 ? 'plastic_crate' : (r2 < 0.75 ? 'metal_painted' : 'metal_rusted');
            waterTank(w, {
              x: px, y, z: pz, rotY: face, r: rng.range(0.44, 0.62), h: rng.range(0.75, 1.05),
              stand: clear * rng.range(0.7, 1.05), mat, solid,
              tint: mat === 'plastic_crate'
                ? [rng.range(0.85, 1.15), rng.range(0.9, 1.06), rng.range(0.8, 1.0)]
                : [rng.range(0.86, 1.14), rng.range(0.86, 1.08), rng.range(0.8, 1.02)],
            });
            break;
          }
          case 4: ventPipe(w, { x: px, y, z: pz, h: clear + rng.range(0.3, 1.2), r: rng.range(0.06, 0.11), rotY: face }); break;
          case 5: laundryPost(w, { x: px, y, z: pz, rotY: face + Math.PI / 2, span: rng.range(2.0, 3.6), h: clear + rng.range(0.5, 1.3), items: rng.int(3, 5) }); break;
          case 6: chimneyStack(w, { x: px, y, z: pz, h: clear + rng.range(0.4, 1.5), wdt: rng.range(0.46, 0.78), solid }); break;
          case 7: roofShed(w, { x: px, y, z: pz, rotY: face, wdt: rng.range(1.2, 1.9), dep: rng.range(0.9, 1.3), h: clear + rng.range(0.15, 0.7) }); break;
          default: {
            // an AC condenser hoisted onto a frame so it straddles the coping
            const fh = clear * rng.range(0.5, 0.85);
            for (const s of [-1, 1]) {
              const lx = px + Math.cos(face) * s * 0.36, lz = pz - Math.sin(face) * s * 0.36;
              const leg = chamferBox(0.07, fh, 0.07, 0.014);
              w.batch.add(leg, 'metal_rusted', { matrix: xform([lx, y + fh / 2, lz], 0), surface: 'metal', uvScale: 0.25 });
              leg.dispose();
            }
            acUnit(w, { x: px, y: y + fh, z: pz, rotY: face, scale: rng.range(0.9, 1.2) });
          }
        }
      }
      placed++; onRun++;
      t += rng.range(1.9, 5.2) / Math.max(0.35, density);
    }
  }
  return placed;
}

/** A dry shrub built from real blades — no alpha map required. */
export function shrub(w, { x, y, z, scale = 1, mat = 'foliage' }) {
  const rng = w.rng;
  const n = Math.round(rng.range(9, 16));
  for (let i = 0; i < n; i++) {
    const a = rng.range(0, Math.PI * 2);
    const g = blade(rng.range(0.35, 0.7) * scale, rng.range(0.05, 0.1) * scale, rng.range(0.3, 0.7), 4, rng.range(-0.6, 0.6));
    w.batch.add(g, mat, {
      matrix: xform([x + Math.cos(a) * rng.range(0, 0.22) * scale, y, z + Math.sin(a) * rng.range(0, 0.22) * scale],
        [rng.range(-0.5, 0.5), a, 0], 1),
      surface: 'foliage', uv: 'keep', cast: true,
      tint: [rng.range(0.42, 0.62), rng.range(0.40, 0.58), rng.range(0.20, 0.36)],
    });
    g.dispose();
  }
}
